/**
 * ArchivOS MUI theme, built on top of the XD tokens (`./tokens.ts`).
 *
 * This layer only translates tokens into MUI vocabulary (palette, typography,
 * breakpoints, generic component overrides). It does not style screens:
 * the existing pages keep applying their own `sx`.
 */
import { createElement, CSSProperties } from 'react';
import { createTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import {
  breakpoints,
  characterStyles,
  colors,
  fontFamilies,
  fontWeights,
  radii,
  CharacterStyle,
} from './tokens';

// ---------------------------------------------------------------------------
// Module augmentation: custom typography variants and breakpoints
// ---------------------------------------------------------------------------

declare module '@mui/material/styles' {
  interface TypographyVariants {
    /** XD `.lien-bouton-niv-1` — Poppins bold 14/21, #FF8B6F. */
    actionLink: CSSProperties;
    /** XD `.lien-bouton-niv2` — Poppins bold 11/17, #FF8B6F. */
    actionLinkSmall: CSSProperties;
  }
  interface TypographyVariantsOptions {
    actionLink?: CSSProperties;
    actionLinkSmall?: CSSProperties;
  }
  interface BreakpointOverrides {
    /** ROADMAP mobile/tablet boundary (768). See note in `breakpoints`. */
    tablet: true;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    actionLink: true;
    actionLinkSmall: true;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * An XD style without its colour. The standard MUI variants (h1, body1…)
 * are used over light and dark backgrounds; the colour is decided by each screen.
 */
const withoutColor = ({ color, ...rest }: CharacterStyle) => rest;

/**
 * Opt-in class for the XD modal pattern (blue `#00409D` paper, white text,
 * title in `en-tete`). It is opt-in rather than a global `MuiDialog` override
 * because the current dialogs (delete confirmations, "Renommer", "Reset
 * password") use `color="primary"` text buttons and fields with dark text:
 * over a blue paper they would be invisible. When those screens are
 * redesigned, it will be enough to add this class to the `Dialog`.
 *
 *   <Dialog className={BRAND_DIALOG_CLASS} …>
 */
export const BRAND_DIALOG_CLASS = 'ArchivOS-Dialog-brand';

// ---------------------------------------------------------------------------
// Theme
// ---------------------------------------------------------------------------

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: colors.primary,
      contrastText: colors.white,
    },
    secondary: {
      main: colors.accent,
      light: colors.accentLight,
      contrastText: colors.white,
    },
    background: {
      default: colors.surface,
      paper: colors.surface,
    },
    text: {
      primary: colors.textBase,
      secondary: colors.muted,
    },
    divider: colors.border,
  },

  /**
   * XD style → MUI variant mapping. The variants without a match in the
   * XD (h3–h6, subtitle2, overline) keep MUI's default scale,
   * already in Poppins. The XD colours are read from `characterStyles`.
   */
  typography: {
    fontFamily: fontFamilies.sans,
    h1: withoutColor(characterStyles.appTitle), // .titre-app
    h2: withoutColor(characterStyles.heading), // .en-tete
    subtitle1: withoutColor(characterStyles.tableHeader), // .en-tête-tableau
    body1: withoutColor(characterStyles.body), // .texte-base
    body2: withoutColor(characterStyles.fieldOnPrimaryLevel2), // .champ-niv-2
    caption: withoutColor(characterStyles.fieldOnPrimaryLevel3), // .champ-blanc-niv-3
    button: {
      ...withoutColor(characterStyles.buttonLarge), // .texte-gros-bouton
      textTransform: 'none',
    },
    // Custom variants: here the colour IS part of the style (they are links).
    actionLink: { ...characterStyles.actionLink }, // .lien-bouton-niv-1
    actionLinkSmall: { ...characterStyles.actionLinkSmall }, // .lien-bouton-niv2
  },

  /**
   * Breakpoints.
   *
   * The ROADMAP decision (Part 2) is mobile `< 768`, tablet `768–1199`,
   * desktop `>= 1200`:
   *   - `tablet` (768) is the ROADMAP mobile/tablet boundary → `down('tablet')`.
   *   - `lg` (1200) already matches the ROADMAP desktop boundary → `up('lg')`.
   *   - `sm` (600) and `md` (900) are MUI's default values, with no direct
   *     use in the live code; do not use them in new screens.
   *
   * The pre-redesign pages that read `MEDIUM_SIZE_SCREEN_WIDTH` (899.95),
   * `INTERMEDIATE_SIZE_SCREEN_WIDTH` (1401) and `down('md')` (MyDocuments,
   * AllDocuments, Step2With/WithoutQRCode) were removed along with those two
   * constants; all the live code converges on `tablet`/`lg`.
   *
   * Last pending step of the convergence: move `sm` to 768 and `md` to 1200
   * and drop `tablet`, once it can be verified that no internal MUI component
   * changes its layout because of it.
   */
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      tablet: breakpoints.tablet,
      md: 900,
      lg: breakpoints.desktop,
      xl: 1536,
    },
  },

  shape: {
    borderRadius: radii.base,
  },

  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: radii.button,
          padding: '10px 24px',
          textTransform: 'none',
        },
        // XD primary button: solid orange, white text.
        containedSecondary: ({ theme: t }) => ({
          backgroundColor: colors.accent,
          color: colors.white,
          '&:hover': {
            backgroundColor: t.palette.secondary.dark,
          },
        }),
        // XD secondary button: outlined. Border at the full colour (MUI
        // uses the primary at 50 % by default) over light backgrounds…
        outlinedPrimary: {
          borderColor: colors.primary,
        },
        // …and `color="inherit"` over blue backgrounds, where the text is white
        // and the border is orange (XD).
        outlinedInherit: {
          borderColor: colors.accent,
          '&:hover': { borderColor: colors.accent },
        },
      },
    },

    // XD table header: orange background, white 600 text (.en-tête-tableau).
    // Only affects <Table>; the current DataGrids have their own classes
    // and will be handled when each listing is redesigned.
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: colors.accent,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          ...withoutColor(characterStyles.tableHeader),
          // XD renders the table header in the brand serif (same family as
          // the "archiv.OS" title), not in Poppins like the token says.
          fontFamily: fontFamilies.serif,
          fontWeight: fontWeights.bold,
          color: colors.white,
          backgroundColor: colors.accent,
          borderBottom: 'none',
        },
      },
    },

    // XD chips: with a border and a "✕" to remove. The icon is changed by
    // default (the current chips with `onDelete` go from the crossed-out circle
    // to a cross); MUI's default variant (`filled`) is not touched so as not to
    // alter the AdminEntities status chip.
    MuiChip: {
      defaultProps: {
        deleteIcon: createElement(CloseIcon),
      },
      styleOverrides: {
        // XD: the criteria / filter chips are outlined in blue.
        outlined: {
          borderColor: colors.primary,
        },
        deleteIcon: {
          color: 'inherit',
          opacity: 0.7,
          '&:hover': {
            color: 'inherit',
            opacity: 1,
          },
        },
      },
    },

    // XD modal — opt-in by class, see BRAND_DIALOG_CLASS.
    MuiDialog: {
      styleOverrides: {
        root: {
          [`&.${BRAND_DIALOG_CLASS} .MuiDialog-paper`]: {
            backgroundColor: colors.primary,
            backgroundImage: 'none',
            color: colors.white,
          },
          [`&.${BRAND_DIALOG_CLASS} .MuiDialogTitle-root`]: {
            ...withoutColor(characterStyles.heading),
            // XD: the modal title shares the content's font (Poppins), not
            // the brand serif of the heading style.
            fontFamily: fontFamilies.sans,
            fontWeight: fontWeights.semiBold,
            color: colors.white,
          },
          // XD: the modal's buttons are less rounded than the global ones.
          [`&.${BRAND_DIALOG_CLASS} .MuiButton-root`]: {
            borderRadius: 6,
          },
          [`&.${BRAND_DIALOG_CLASS} .MuiDialogContentText-root`]: {
            color: colors.white,
          },
          [`&.${BRAND_DIALOG_CLASS} .MuiDialogContent-dividers`]: {
            borderColor: 'rgba(255, 255, 255, 0.24)',
          },
        },
      },
    },
  },
});

export default theme;
